import DSBox from '../../design-system/DSBox';
import DSTextField from '../../design-system/DSTextField';
import DSCircularProgress from '../../design-system/DSCircularProgress';

const GIFT_INPUT_SX = {
  backgroundColor: '#212121',
  '.MuiInputBase-input': { fontSize: '15px' },
} as const;
import { DSTypography } from '../../design-system/Typography';
import DSAvatar from '../../design-system/DSAvatar';
import DSIconButton from '../../design-system/DSIconButton';
import DSButton from '../../design-system/DSButton';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { KLYDO_BLACK } from '../../consts/colors';
import useGiftForm from './hooks/useGiftForm';
import GenerateGreetDialog from './GenerateGreet/components/GenerateGreetDialog';
import { isMobile } from 'react-device-detect';
import {
  MAX_GREETING_LENGTH,
  MAX_GREETING_ROWS,
  MAX_SENDER_NAME_LENGTH,
} from './consts';

const GiftForm = () => {
  const {
    theme,
    greeting,
    name,
    showSenderName,
    profileUrl,
    fileInputRef,
    klydoGiftProps,
    defaultPhoto,
    handleAvatarClick,
    handleGreetChange,
    handleNameChange,
    handleDateChange,
    isUploading,
    handleRemoveProfile,
    handlePickProfile,
    linesLimit,
    handleGreetPaste,
    handleClickGenerateGreet,
    generateGreetDialogOpen,
    handleCloseGenerateGreetDialog,
    handleSaveGenerateGreet,
  } = useGiftForm();

  return (
    <DSBox
      width="100%"
      sx={{
        backgroundColor: theme.palette.background.paper,
        paddingTop: { xs: 2, md: 10 },
        paddingBottom: { xs: 10, md: 0 },
      }}
    >
      <DSBox
        gap={isMobile ? 0.5 : 5}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: isMobile ? 'column' : 'row-reverse',
          alignItems: 'center',
          textAlign: 'left',
        }}
      >
        {/* Photo */}
        <DSBox
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <DSTypography
            sx={{
              lineHeight: '18.96px',
              fontSize: '16px',
              fontFamily: 'Gilroy-SemiBold',
              color: '#C9C9C9',
            }}
          >
            {profileUrl !== defaultPhoto ? 'Change photo' : 'Add your photo'}
          </DSTypography>
          <DSBox py={0.5} />
          <DSBox
            gap={0}
            position="relative"
            display="inline-block"
            sx={{ textAlign: 'right' }}
          >
            {profileUrl !== defaultPhoto && (
              <DSIconButton
                onClick={handleRemoveProfile}
                sx={{
                  backgroundColor: 'transparent',
                  borderRadius: '50%',
                  position: 'absolute',
                  left: 110,
                  top: -13,
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                <ClearIcon />
              </DSIconButton>
            )}
            <DSAvatar
              onClick={handleAvatarClick}
              sx={{
                width: 140,
                height: 140,
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <img
                src={profileUrl}
                alt="Sender"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  border: '3px solid white',
                  backgroundColor: KLYDO_BLACK,
                  opacity: isUploading ? 0.5 : 1,
                }}
              />
              {isUploading && (
                <DSBox
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                  }}
                >
                  <DSCircularProgress size={32} />
                </DSBox>
              )}
            </DSAvatar>
            {profileUrl === defaultPhoto && (
              <DSBox
                onClick={handleAvatarClick}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  cursor: 'pointer',
                  '&:hover': { transform: 'scale(1.1)' },
                }}
              >
                <AddIcon sx={{ fontSize: 90, opacity: 0.75 }} />
              </DSBox>
            )}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".jpg,.jpeg,.png"
              onChange={handlePickProfile}
            />
          </DSBox>
        </DSBox>

        {/* Form fields */}
        <DSBox
          width={isMobile ? '80%' : '350px'}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            textAlign: 'left',
          }}
        >
          {showSenderName && (
            <>
              <DSBox>
                <DSTypography
                  sx={{
                    lineHeight: '18.96px',
                    fontSize: '16px',
                    color: '#C9C9C9',
                    fontFamily: 'Gilroy-SemiBold',
                  }}
                >
                  *From
                </DSTypography>
                <DSBox py={0.5} />
                <DSTextField
                  placeholder="Dad"
                  value={name}
                  fullWidth
                  onChange={(e) => handleNameChange(e.target.value)}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: MAX_SENDER_NAME_LENGTH },
                    },
                  }}
                  sx={GIFT_INPUT_SX}
                />
              </DSBox>

              <DSBox py={1} />
            </>
          )}

          <DSBox>
            <DSTypography
              sx={{
                lineHeight: '18.96px',
                fontSize: '16px',
                color: '#C9C9C9',
                fontFamily: 'Gilroy-SemiBold',
              }}
            >
              Write your greeting
            </DSTypography>
            <DSBox py={0.5} />
            <DSTextField
              placeholder="Dear Ron..."
              value={greeting}
              onChange={(e) => handleGreetChange(e.target.value)}
              multiline
              fullWidth
              rows={5}
              maxRows={8}
              slotProps={{
                input: {
                  style: { padding: '20px' },
                  onKeyDown: linesLimit,
                  onPaste: handleGreetPaste,
                },
              }}
              sx={{
                ...GIFT_INPUT_SX,
                '.MuiInputBase-input': {
                  overflowWrap: 'break-word',
                  fontSize: '15px',
                  maxWidth: '300px',
                  display: 'block',
                },
              }}
            />
            <DSBox py={0.5} />
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <DSBox
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
              >
                <DSTypography
                  sx={{
                    lineHeight: '18.96px',
                    fontSize: '16px',
                    color: '#C9C9C9',
                    fontFamily: 'Gilroy-SemiBold',
                  }}
                >
                  {greeting.length}/{MAX_GREETING_LENGTH}
                </DSTypography>
                <DSTypography
                  sx={{
                    fontSize: '12px',
                    color: '#A5A5A5',
                    fontFamily: 'Gilroy-Medium',
                  }}
                >
                  Limit: {MAX_GREETING_ROWS} rows
                </DSTypography>
              </DSBox>
              <DSButton
                variant="text"
                onClick={handleClickGenerateGreet}
                sx={{
                  textDecoration: 'underline',
                  fontFamily: 'Gilroy-Medium',
                  fontSize: '14px',
                  color: '#C9C9C9',
                  textTransform: 'none',
                  margin: 0,
                  padding: 0,
                }}
              >
                Generate Greet with AI
              </DSButton>
            </DSBox>
          </DSBox>

          <DSBox py={1} />

          <DSBox>
            <DSTypography
              sx={{
                lineHeight: '18.96px',
                fontSize: '16px',
                color: '#C9C9C9',
                fontFamily: 'Gilroy-SemiBold',
              }}
            >
              Date of occasion
            </DSTypography>
            <DSBox py={0.5} />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={!klydoGiftProps.date ? 'MM/DD/YYYY' : ''}
                value={
                  klydoGiftProps?.date
                    ? dayjs(new Date(klydoGiftProps.date))
                    : null
                }
                onChange={handleDateChange}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '28px',
                    backgroundColor: '#212121',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiInputBase-input': { paddingLeft: '16px' },
                }}
              />
            </LocalizationProvider>
          </DSBox>
        </DSBox>
      </DSBox>

      <GenerateGreetDialog
        open={generateGreetDialogOpen}
        onClose={handleCloseGenerateGreetDialog}
        onSave={handleSaveGenerateGreet}
      />
    </DSBox>
  );
};

export default GiftForm;
